import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { downloadMd } from './farm-setup-shared';
import { WSL_DISTRO, winKeepAliveCmd, winSystemdCmd, winUsbAttachCmd, winWslInstallCmd } from './farm-docker-config';
/**
 * Windows host = WSL2 Ubuntu running the regular Linux recipe, so the Docker steps
 * reuse the Linux sub-tab's commands verbatim and only the WSL/USB plumbing is added.
 */
export function buildWindowsSetup(name, std) {
    const cmds = {
        wsl: winWslInstallCmd(),
        systemd: winSystemdCmd(),
        keepalive: winKeepAliveCmd(),
        install: std.installCmd,
        updater: std.updaterCmd,
        cleanup: std.cleanupCmd,
        run: std.dockerRun,
        usb: winUsbAttachCmd(),
    };
    const block = (lang, cmd) => `\`\`\`${lang}\n${cmd}\n\`\`\``;
    const mdContent = `# Farm setup (Windows) — ${name}

The farm runs on Docker Engine inside WSL2 (${WSL_DISTRO}). Do not use Docker Desktop:
its daemon can't see \`~/.mobile-farm/mf-update.sh\` in the distro, so **Restart with update**
does nothing. Quit Docker Desktop and turn off its WSL integration before you start.

## 1. Install WSL2 + usbipd-win (PowerShell as Administrator)

Reboot if asked, then open **${WSL_DISTRO}** from the Start menu and create a user.

${block('powershell', cmds['wsl'])}

## 2. Enable systemd (in Ubuntu)

${block('bash', cmds['systemd'])}

## 3. Keep WSL running (PowerShell as Administrator)

${block('powershell', cmds['keepalive'])}

## 4. Install Docker (in Ubuntu)

${block('bash', cmds['install'])}

## 5. Create the self-update helper (in Ubuntu)

${block('bash', cmds['updater'])}

## 6. Stop and remove the existing container and image (in Ubuntu)

For a manual update start here, then re-run step 7.

${block('bash', cmds['cleanup'])}

## 7. Start the container (in Ubuntu)

${block('bash', cmds['run'])}

## 8. Share USB phones with WSL (PowerShell as Administrator)

Run once per phone. \`--auto-attach\` re-attaches after replugging and must stay running.

${block('powershell', cmds['usb'])}

## 9. Initial setup

Open **http://localhost:8000** in a browser on the farm machine, check the relay connection
in **Settings** and allow USB debugging on each phone.
`;
    return { cmds, mdContent };
}
const STEPS = [
    { text: 'farms.win_step1', copy: 'wsl' },
    { text: 'farms.win_step2', copy: 'systemd' },
    { text: 'farms.win_step3', copy: 'keepalive' },
    { text: 'farms.win_step4', copy: 'install' },
    { text: 'farms.win_step5', copy: 'updater' },
    { text: 'farms.win_step6', copy: 'cleanup' },
    { text: 'farms.win_step7', copy: 'run' },
    { text: 'farms.win_step8', copy: 'usb' },
];
/** Inner HTML for the Windows sub-tab; copy/download wired by {@link wireWindowsSetup}. */
export function windowsSetupHtml(s) {
    const steps = STEPS.map((st, i) => `
        <p class="modal__text"${i ? ' style="margin-top:12px"' : ''}>${t(st.text, { distro: WSL_DISTRO })}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.cmds[st.copy] ?? '')}</pre>
            <button class="tbl-btn copy-key-btn" data-win-copy="${st.copy}" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>`).join('');
    return `
        <p class="modal__text" style="margin-bottom:12px">${t('farms.win_intro', { distro: WSL_DISTRO })}</p>
        ${steps}
        <p class="modal__text" style="margin-top:12px">${t('farms.win_step9')}</p>
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.launch_li1')}</li>
            <li>${t('farms.launch_li2')}</li>
            <li>${t('farms.launch_li3')}</li>
        </ul>
        <div class="modal__actions">
            <button type="button" class="tbl-btn" data-win-download style="margin-right:auto">${t('farms.download_md')}</button>
        </div>
    `;
}
export function wireWindowsSetup(root, s, name) {
    root.querySelectorAll('[data-win-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const cmd = s.cmds[btn.dataset['winCopy'] ?? ''];
            if (cmd === undefined)
                return;
            void navigator.clipboard.writeText(cmd);
            btn.textContent = `✓ ${t('common.copied')}`;
            setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
        });
    });
    root.querySelector('[data-win-download]')?.addEventListener('click', () => {
        downloadMd(s.mdContent, `farm-setup-windows-${name}`);
    });
}
